<div class="document-generation-control">
  @if (!templatesLoaded) {
    <div class="loading-state">Loading document templates…</div>
  } @else {
    <mat-form-field appearance="outline" class="compact full-width">
      <mat-label>Add a document</mat-label>
      <input matInput
             type="text"
             [formControl]="searchControl"
             [placeholder]="placeholder"
             [matAutocomplete]="auto">
      <mat-icon matSuffix fontSet="fa" fontIcon="fa-magnifying-glass"></mat-icon>
      <mat-autocomplete #auto="matAutocomplete" class="dgc-autocomplete-panel" (optionSelected)="onOptionSelected($event)">
        @for (group of filteredGroups(); track group.type) {
          <mat-optgroup [label]="group.type | uppercase">
            @for (t of group.items; track t.id) {
              <mat-option [value]="t.id" class="dgc-option">
                <div class="dgc-option__row">
                  <span class="dgc-option__name">{{ t.name }}</span>
                  @if (t.form_type) {
                    <span class="dgc-badge">{{ t.form_type }}</span>
                  }
                  @if (t.docusign_compatible) {
                    <span class="dgc-badge dgc-badge--docusign" tp="DocuSign compatible">DocuSign</span>
                  }
                </div>
                @if (t.description || t.state || t.year) {
                  <div class="dgc-option__meta">
                    @if (t.description) {
                      <span class="dgc-option__desc">{{ t.description }}</span>
                    }
                    @if (t.state || t.year) {
                      <span class="dgc-option__tags">
                        @if (t.state) { <span class="dgc-tag">{{ t.state }}</span> }
                        @if (t.year) { <span class="dgc-tag">{{ t.year }}</span> }
                      </span>
                    }
                  </div>
                }
              </mat-option>
            }
          </mat-optgroup>
        }
      </mat-autocomplete>
    </mat-form-field>

    @if (templatesLoadError) {
      <div class="load-error">
        <mat-icon fontSet="fa" fontIcon="fa-circle-exclamation" class="mr-1"></mat-icon>
        Document templates could not be loaded. Please try again later.
      </div>
    } @else if (noTemplatesAvailable) {
      <div class="info-state">No document templates are available for your organization.</div>
    } @else if (atCap) {
      <div class="max-reached">Maximum of {{ maxEntries }} documents reached.</div>
    }

    @for (row of displayRows; track row.control) {
      <div class="entry-row" [class.entry-row--unresolved]="row.state !== 'resolved'">
        <div class="entry-main">
          @if (row.template; as tpl) {
            <div class="entry-info">
              <div class="entry-info__row">
                @if (tpl.type) {
                  <span class="dgc-badge dgc-badge--type">{{ tpl.type }}</span>
                }
                <span class="entry-name">{{ tpl.name }}</span>
                @if (tpl.form_type) {
                  <span class="dgc-badge">{{ tpl.form_type }}</span>
                }
                @if (tpl.disabled) {
                  <span class="dgc-badge dgc-badge--warn"
                        tp="This template is disabled, so it will NOT be generated when this step is completed. It stays configured here until you remove it — re-enable the template to start generating it again.">Disabled</span>
                }
                @if (tpl.docusign_compatible) {
                  <span class="dgc-badge dgc-badge--docusign" tp="DocuSign compatible">DocuSign</span>
                }
                @if (tpl.state) { <span class="dgc-tag">{{ tpl.state }}</span> }
                @if (tpl.year) { <span class="dgc-tag">{{ tpl.year }}</span> }
              </div>
              @if (tpl.description) {
                <div class="entry-desc">{{ tpl.description }}</div>
              }
              @if (mode === 'rule' && hasRule(row.control)) {
                <!--
                  No tooltip: the summary is spelled out in full right here and wraps rather than
                  truncating, so a tooltip only repeated the sentence the user was already reading.
                -->
                <div class="entry-rule-summary">
                  <mat-icon fontSet="fa" fontIcon="fa-arrows-turn-to-dots"></mat-icon>
                  <span>{{ ruleSummary(row.control) }}</span>
                </div>
              }
            </div>
          } @else {
            <div class="entry-info">
              <div class="entry-info__row">
                <span class="dgc-badge dgc-badge--unavailable">Unavailable</span>
                @if (row.state === 'missing') {
                  <span class="entry-name entry-name--unresolved">Template no longer exists</span>
                } @else {
                  <span class="entry-name entry-name--unresolved">Template details unavailable</span>
                }
              </div>
              <div class="entry-desc entry-desc--muted">
                @if (row.state === 'missing') {
                  This document is still configured on the step but its template has been deleted or is no
                  longer available to your organization. Remove it to clear it from this step.
                } @else {
                  This document is configured on the step, but its template could not be loaded just now.
                  It has not been changed — reload to try again.
                }
                <span class="entry-id">{{ row.templateId }}</span>
              </div>
            </div>
          }

          @if (mode === 'rule' && row.template) {
            <button class="ee__button gray-bg" mat-icon-button type="button"
                    aria-label="Document rule"
                    [disabled]="readOnly"
                    (click)="openRuleDialog(row.control)"
                    [class.has-rule]="hasRule(row.control)"
                    [tp]="hasRule(row.control)
                          ? 'Conditional rule set — only generate when the condition is met. Click to edit.'
                          : 'Add a conditional rule for this document'">
              <mat-icon fontSet="fa" fontIcon="fa-arrows-turn-to-dots"></mat-icon>
            </button>
          }

          @if (row.template) {
            @if (row.control.controls.hide_from_client.value) {
              <span class="dgc-badge dgc-badge--hidden-from-client"
                    tp="This document will NOT be visible to the client">Hidden from client</span>
            }
            <button class="ee__button gray-bg" mat-icon-button type="button"
                    aria-label="Toggle document visibility"
                    [disabled]="readOnly"
                    (click)="toggleVisible(row.index)"
                    [tp]="!row.control.controls.hide_from_client.value
                          ? 'Visible to client. Click to hide from client.'
                          : 'Firm-only. Click to make visible to client.'">
              <mat-icon fontSet="fa"
                        [fontIcon]="!row.control.controls.hide_from_client.value ? 'fa-toggle-on' : 'fa-toggle-off'"></mat-icon>
            </button>
          }

          <!-- Removal stays available for a missing template so the user can clean up the dangling
               reference. Withheld while the state is unknown — we do not know what we would discard. -->
          @if (row.state !== 'unknown') {
            <button class="ee__button gray-bg" mat-icon-button type="button"
                    aria-label="Remove document" tp="Remove document"
                    [disabled]="readOnly"
                    (click)="removeEntry(row.index)">
              <mat-icon fontSet="fa" fontIcon="fa-trash"></mat-icon>
            </button>
          }
        </div>
      </div>
    }
  }
</div>
